import React, { useState } from "react";
import validate from "validate.js";
import { EyeIcon, EyeSlashIcon, XMarkIcon } from "@heroicons/react/24/solid";

export type InputType =
	| "text"
	| "email"
	| "password"
	| "tel"
	| "url"
	| "number"
	| "domain";

interface IInputProps {
	id?: string;
	label: string;
	type: InputType;
	value?: string;
	onChange: (value: string, isValid?: boolean) => void;
	required?: boolean;
	disabled?: boolean;
	readOnly?: boolean;
	noCross?: boolean;
	validations?: ((value: string) => string | null)[]; // Custom validation functions
	useBuiltInValidations?: boolean;
	validateOnBlur?: boolean;
	helpText?: string;
	maxLength?: number;
	noBorder?: boolean;
	allowedPattern?: RegExp;
	disallowedPattern?: RegExp;
}

const getBuiltInConstraint = (
	type: InputType,
	label: string,
	required: boolean,
	allowedPattern?: RegExp,
	disallowedPattern?: RegExp
) => {
	const constraints: any = {};

	if (required) {
		constraints.presence = {
			allowEmpty: false,
			message: `^${label} is required`,
		};
	}

	switch (type) {
		case "email":
			constraints.format = {
				pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
				message: "^Please enter a valid email address",
			};
			break;
		case "url":
			constraints.url = { message: "^Please enter a valid URL" };
			break;
		case "tel":
			constraints.format = {
				pattern: /^\+[1-9]\d{7,14}$/,
				message:
					"^Please enter a valid phone number in E.164 format (e.g., +61412345678)",
			};
			break;
		case "domain":
			constraints.format = {
				pattern:
					/^(?=.{1,253}$)(?!-)([a-zA-Z0-9-]{1,63}\.)+[a-zA-Z]{2,63}(:\d{1,5})?$/,
				message: "^Please enter a valid domain name",
			};
			break;
		case "text":
			if (allowedPattern) {
				constraints.format = {
					pattern: allowedPattern,
					message: `^${label} has invalid characters`,
				};
			} else if (disallowedPattern) {
				constraints.format = {
					pattern: new RegExp(
						`^(?!.*${disallowedPattern.source}).*$`
					),
					message: "^Contains invalid characters",
				};
			}
			break;
	}

	return constraints;
};

const InputBox: React.FC<IInputProps> = ({
	id,
	label,
	type,
	value = "",
	onChange,
	required = false,
	disabled = false,
	readOnly = false,
	noCross = false,
	validations = [],
	useBuiltInValidations = true,
	validateOnBlur = true,
	helpText,
	maxLength,
	noBorder = false,
	allowedPattern,
	disallowedPattern,
}) => {
	const [showPassword, setShowPassword] = useState(false);
	const [validationError, setValidationError] = useState<string | null>(null);
	const [touched, setTouched] = useState(false);
	const togglePasswordVisibility = () => setShowPassword(!showPassword);
	const inputId = id ?? `input-${label.replace(/\s+/g, "-").toLowerCase()}`;
	const runValidations = (currentValue: string): boolean => {
		let errors: string[] = [];

		if (useBuiltInValidations) {
			const builtIn = getBuiltInConstraint(
				type,
				label,
				required,
				allowedPattern,
				disallowedPattern
			);
			const result = validate(
				{ [label]: currentValue },
				{ [label]: builtIn }
			);
			if (result && result[label]) {
				errors = errors.concat(result[label]);
			}
		}

		for (const validateFn of validations) {
			const error = validateFn(currentValue);
			if (error) errors.push(error);
		}

		if (errors.length > 0) {
			setValidationError(errors[0]);
			return false;
		}

		setValidationError(null);
		return true;
	};

	const handleBlur = () => {
		setTouched(true);
		const isValid = runValidations(value);
		onChange(value, isValid);
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value;
		const isValid = runValidations(newValue);
		onChange(newValue, isValid);
		if (!validateOnBlur) {
			runValidations(newValue);
		}
	};

	return (
		<div
			className={`bg-slate-50 px-2 pb-2 relative z-0 w-full flex flex-col items-start justify-center ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
		>
			<div className="w-full">
				<div className="relative w-full">
					<div className="flex items-center justify-between gap-2 w-full">
						<div>
							<span className="font-Poppins font-semibold text-sm">
								{label}
							</span>
							{required && (
								<span className="font-Poppins text-lg text-red-500">
									{" "}
									*
								</span>
							)}
						</div>
						{maxLength && (
							<span className="font-Poppins font-medium text-xs">
								{value.length}/{maxLength}
							</span>
						)}
					</div>
					<div
						className={`flex items-center justify-between w-full border-0 ${noBorder ? "" : "border-b-2"} border-gray-400`}
					>
						<input
							id={inputId}
							data-testid={inputId}
							type={
								type === "password" && !showPassword
									? "password"
									: "text"
							}
							value={value}
							onChange={handleChange}
							onBlur={handleBlur}
							className={`block pt-1 w-full ${type === "number" || type === "tel" ? "font-code" : "font-Poppins"} text-md tracking-wider font-normal text-md text-gray-900 bg-transparent  appearance-none focus:outline-none focus:ring-0 focus:border-sky-600 peer`}
							placeholder={""}
							disabled={disabled}
							readOnly={readOnly}
							maxLength={maxLength}
							onFocus={() => setTouched(true)}
						/>
						<div className="flex items-center gap-2">
							{type === "password" && value && (
								<span
									className="h-full flex items-center cursor-pointer"
									onClick={togglePasswordVisibility}
								>
									{showPassword ? (
										<EyeSlashIcon className="w-5 h-5 text-gray-500" />
									) : (
										<EyeIcon className="w-5 h-5 text-gray-500" />
									)}
								</span>
							)}
							{!readOnly && !disabled && value && !noCross && (
								<button
									type="button"
									className="flex items-center cursor-pointer"
									onClick={() => onChange("")}
									disabled={disabled}
								>
									<XMarkIcon className="w-5 h-5 text-gray-500" />
								</button>
							)}
						</div>
					</div>

					{/* <div className="flex items-center justify-end pt-1">
						<span className="font-mono text-xs text-gray-500">
							{value.length}/{maxLength}
						</span>
					</div> */}
					{helpText && (
						<div className="w-full flex items-center justify-start pt-1">
							<span className="font-Poppins font-semibold tracking-wider text-xs text-sky-500">
								{helpText}
							</span>
						</div>
					)}

					{/* <label
						htmlFor={inputId}
						className={`absolute w-full font-Poppins font-medium text-md text-gray-900 duration-300 transform scale-75 -translate-y-6 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-sky-900 ${
							value
								? "scale-75 -translate-y-6 left-0 text-gray-800 font-Poppins tracking-wider"
								: "peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 left-0"
						}`}
					>
						<div className="flex items-center justify-between w-full gap-2">
							<div className="flex items-center">
								{label}

								{required && (
									<span className="font-Poppins text-lg text-red-500">
										{" "}
										*
									</span>
								)}
								<span className="font-mono text-xs text-gray-500">
									{value.length}/{maxLength}
								</span>
							</div>
						</div>
					</label> */}
				</div>

				{/* Clear Button */}
				{/* {!readOnly && !disabled && value && !noCross && (
					<button
						type="button"
						className="absolute right-2 -top-2 h-full flex items-center cursor-pointer"
						onClick={() => onChange("")}
						disabled={disabled}
					>
						<XMarkIcon className="w-5 h-5 text-gray-500" />
					</button>
				)} */}

				{/* Password visibility toggle */}
				{/* {type === "password" && value && (
					<span
						className="absolute right-10 top-0 h-full flex items-center cursor-pointer"
						onClick={togglePasswordVisibility}
					>
						{showPassword ? (
							<EyeSlashIcon className="w-5 h-5 text-gray-500" />
						) : (
							<EyeIcon className="w-5 h-5 text-gray-500" />
						)}
					</span>
				)} */}
			</div>

			{/* Validation error display */}
			{touched && validationError && (
				<div className="w-full flex items-center justify-start pt-1">
					<span className="font-semibold text-xs text-red-500">
						{validationError}
					</span>
				</div>
			)}
		</div>
	);
};

export default InputBox;
